import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Pulse,
  ArrowLeft,
  MagnifyingGlass,
  ShieldCheckered,
  Globe,
  ArrowsClockwise,
  LockKey,
  EnvelopeSimple,
  SignOut,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const flag = (cc) => {
  if (!cc || cc.length !== 2) return "";
  const A = 127397;
  return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => c.charCodeAt(0) + A));
};

// =====================
// Admin Login Form
// =====================
const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/login`,
        { email: email.trim(), password },
        { withCredentials: true }
      );
      toast.success("Sesión admin iniciada");
      onSuccess(res.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429) {
        setError(detail || "Demasiados intentos");
      } else if (status === 401) {
        setError("Credenciales inválidas");
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("No se pudo iniciar sesión");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <ShieldCheckered size={28} weight="duotone" className="text-aqua" />
            <span className="font-heading font-black text-base tracking-[0.4em]">
              AQUÍSIGO · ADMIN
            </span>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            Acceso restringido
          </h1>
          <p className="text-white/60 text-sm mt-2">
            Ingresa tus credenciales de administrador.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-surface border border-border rounded-2xl p-6 space-y-4"
          data-testid="admin-login-form"
        >
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-1.5 block flex items-center gap-1.5">
              <EnvelopeSimple size={12} weight="bold" /> Correo
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@ejemplo.com"
              autoComplete="email"
              required
              className="bg-background border-border text-white mono font-mono text-sm"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-white/55 mb-1.5 block flex items-center gap-1.5">
              <LockKey size={12} weight="bold" /> Contraseña
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              required
              className="bg-background border-border text-white"
              data-testid="admin-login-password"
            />
          </div>

          {error && (
            <div
              className="px-3 py-2 rounded-md bg-danger/10 border border-danger/40 text-danger text-sm"
              data-testid="admin-login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-aqua text-background hover:bg-aqua/90"
            data-testid="admin-login-submit"
          >
            <LockKey size={14} weight="bold" className="mr-1.5" />
            {submitting ? "Verificando..." : "Iniciar sesión"}
          </Button>

          <p className="text-[11px] text-center text-white/40 pt-1">
            Tras 5 intentos fallidos, la cuenta se bloquea por 15 minutos.
          </p>
        </form>
      </div>
    </div>
  );
};

// =====================
// Admin Users Table
// =====================
const AdminUsersTable = ({ onLogout }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchUsers = async (silent = false) => {
    if (!silent) setLoading(true);
    if (silent) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        onLogout();
        return;
      }
      toast.error("No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
    } catch {
      /* ignore */
    }
    toast.success("Sesión admin cerrada");
    onLogout();
  };

  const handleRefresh = async () => {
    await fetchUsers(true);
    toast.success("Lista actualizada");
  };

  const filtered = users.filter((u) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.country?.toLowerCase().includes(q) ||
      u.city?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60 text-sm">
        Cargando panel admin...
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full text-white" data-testid="admin-page">
      <header className="w-full border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/70 hover:text-aqua transition-colors"
            data-testid="admin-back"
          >
            <ArrowLeft size={18} weight="bold" />
            <span className="text-sm">Inicio</span>
          </button>
          <div className="flex items-center gap-3">
            <ShieldCheckered size={22} weight="duotone" className="text-aqua" />
            <span className="font-heading font-black text-base tracking-[0.4em]">
              AQUÍSIGO · ADMIN
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="border-border text-white/70 hover:text-danger hover:border-danger/50"
            data-testid="admin-logout-btn"
          >
            <SignOut size={14} className="mr-1.5" />
            Salir
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <p className="text-xs uppercase tracking-[0.4em] text-white/50 mb-3">
          Panel de administración
        </p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mb-2">
          Usuarios <span className="text-aqua">registrados</span>
        </h1>
        <p className="text-white/60 mb-8">
          {total} {total === 1 ? "cuenta" : "cuentas"} con acceso a AquíSigo.
        </p>

        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-white/45"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, correo, país..."
              className="bg-surface border-border text-white pl-10"
              data-testid="admin-search"
            />
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-border text-white/70 hover:border-aqua/50 hover:text-aqua"
            data-testid="admin-refresh"
          >
            <ArrowsClockwise
              size={14}
              weight="bold"
              className={"mr-1.5 " + (refreshing ? "animate-spin" : "")}
            />
            Actualizar
          </Button>
        </div>

        {filtered.length === 0 ? (
          <div
            className="bg-surface border border-dashed border-border rounded-2xl p-10 text-center text-white/45"
            data-testid="admin-empty"
          >
            {query ? "Sin resultados para tu búsqueda." : "Aún no hay usuarios registrados."}
          </div>
        ) : (
          <div
            className="bg-surface border border-border rounded-2xl overflow-hidden"
            data-testid="admin-users-table"
          >
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-background/50">
                    <th className="text-left px-5 py-3 text-[10px] font-bold text-white/45 uppercase tracking-[0.2em]">
                      Usuario
                    </th>
                    <th className="text-left px-5 py-3 text-[10px] font-bold text-white/45 uppercase tracking-[0.2em]">
                      Correo
                    </th>
                    <th className="text-left px-5 py-3 text-[10px] font-bold text-white/45 uppercase tracking-[0.2em]">
                      País / Ciudad
                    </th>
                    <th className="text-left px-5 py-3 text-[10px] font-bold text-white/45 uppercase tracking-[0.2em]">
                      Registro
                    </th>
                    <th className="text-left px-5 py-3 text-[10px] font-bold text-white/45 uppercase tracking-[0.2em]">
                      Última sesión
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => (
                    <tr
                      key={u.user_id}
                      className="border-b border-border/40 last:border-b-0 hover:bg-aqua/[0.03]"
                      data-testid={`admin-user-row-${i}`}
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img
                              src={u.picture}
                              alt=""
                              className="w-8 h-8 rounded-full border border-border"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-aqua/15 border border-aqua/30 text-aqua text-xs font-bold flex items-center justify-center">
                              {(u.name || "U")[0].toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-medium text-white truncate">
                              {u.name || "—"}
                            </div>
                            {u.is_admin && (
                              <div className="text-[10px] uppercase tracking-[0.2em] text-aqua font-semibold">
                                Admin
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 mono font-mono text-[13px] text-white/85 truncate max-w-[260px]">
                        {u.email}
                      </td>
                      <td className="px-5 py-3">
                        {u.country ? (
                          <div className="flex items-center gap-2">
                            <span className="text-base leading-none">
                              {flag(u.country_code)}
                            </span>
                            <div>
                              <div className="text-white">{u.country}</div>
                              {u.city && (
                                <div className="text-xs text-white/50">{u.city}</div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-white/40 inline-flex items-center gap-1.5">
                            <Globe size={12} /> Desconocido
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 mono font-mono text-[12px] text-white/70">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="px-5 py-3 mono font-mono text-[12px] text-white/70">
                        {formatDate(u.last_seen)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden divide-y divide-border/40">
              {filtered.map((u, i) => (
                <div
                  key={u.user_id}
                  className="px-4 py-4"
                  data-testid={`admin-user-card-${i}`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {u.picture ? (
                      <img
                        src={u.picture}
                        alt=""
                        className="w-9 h-9 rounded-full border border-border"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-aqua/15 border border-aqua/30 text-aqua text-sm font-bold flex items-center justify-center">
                        {(u.name || "U")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-white truncate">{u.name || "—"}</div>
                      <div className="mono font-mono text-xs text-white/65 truncate">
                        {u.email}
                      </div>
                    </div>
                    {u.is_admin && (
                      <span className="text-[9px] uppercase tracking-[0.2em] text-aqua font-bold border border-aqua/40 rounded-md px-1.5 py-0.5">
                        Admin
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-white/60 mt-2">
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mb-0.5">
                        País
                      </div>
                      <div className="text-white">
                        {u.country ? `${flag(u.country_code)} ${u.country}` : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40 mb-0.5">
                        Última sesión
                      </div>
                      <div className="mono font-mono text-white/80">
                        {formatDate(u.last_seen)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="mt-4 text-xs text-white/40">
          País detectado vía geolocalización por IP en el momento del primer login. Puede
          ser inexacto si el usuario usa VPN.
        </p>
      </main>
    </div>
  );
};

// =====================
// Top-level wrapper that decides which view to show
// =====================
const AdminPage = () => {
  const [authed, setAuthed] = useState(null); // null=checking, false=show login, true=show table

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { withCredentials: true });
        if (!cancelled) setAuthed(true);
      } catch {
        if (!cancelled) setAuthed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/60 text-sm">
        Verificando sesión...
      </div>
    );
  }

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  return <AdminUsersTable onLogout={() => setAuthed(false)} />;
};

export default AdminPage;
